import * as React from 'react';
import { throttle } from '@cian/newbuilding-utils';
import { getElementMargin } from '../../utils/window';
import { WithChangeOrientation } from '../WithChangeOrientation';
import * as styles from './CollapsedList.css';

const DEFAULT_CONTENT_HEIGHT = 110;

interface ICollapsedListProps {
  isOpened: boolean;
  visibleRows?: number;
  cols?: 1 | 2;
  children: React.ReactNode[];
  containerStyles?: string;
}

interface ICollapsedListState {
  contentHeight: number;
  isInited: boolean;
}

export class CollapsedList extends React.PureComponent<ICollapsedListProps, ICollapsedListState> {
  private contentEl = React.createRef<HTMLDivElement>();

  public state = {
    isInited: false,
    contentHeight: DEFAULT_CONTENT_HEIGHT,
  };

  public componentDidMount() {
    window.addEventListener('resize', this.throttledResizeHandler);
  }

  public componentDidUpdate() {
    this.updateContentHeight();
  }

  public componentWillUnmount() {
    window.removeEventListener('resize', this.throttledResizeHandler);
  }

  public throttledResizeHandler = throttle(300, () => {
    this.updateContentHeight();
  });

  public render() {
    if (!this.props.children) {
      return null;
    }

    const { children, containerStyles } = this.props;
    const { contentHeight } = this.state;

    return (
      <WithChangeOrientation handler={this.updateContentHeight}>
        <div className={styles['content']} style={{ maxHeight: `${contentHeight}px` }}>
          <div className={containerStyles} ref={this.contentEl}>
            {children}
          </div>
        </div>
      </WithChangeOrientation>
    );
  }

  private updateContentHeight = () => {
    this.setState({
      contentHeight: this.getVisibleContentHeight(),
      isInited: true,
    });
  };

  private getVisibleContentHeight = () => {
    const { visibleRows, isOpened, cols = 1 } = this.props;
    const contentEl = this.contentEl.current;
    const nodes = contentEl?.childNodes as HTMLElement[] | null | undefined;

    if (!contentEl || !nodes) {
      return 0;
    }

    if (typeof visibleRows === 'undefined' || isOpened) {
      return contentEl.offsetHeight;
    }

    return Array.from(nodes)
      .slice(0, visibleRows * cols)
      .reduce(
        (acc, el, index) => {
          const { marginTop, marginBottom } = getElementMargin(el);
          const height = el.offsetHeight + marginTop + marginBottom;
          const isLastColumn = (index + 1) % cols === 0 || index === nodes.length - 1;

          return {
            totalHeight: isLastColumn
              ? acc.prevHeight < height
                ? acc.totalHeight + height
                : acc.totalHeight + acc.prevHeight
              : acc.totalHeight,
            prevHeight: isLastColumn ? 0 : height,
          };
        },
        { totalHeight: 0, prevHeight: 0 },
      ).totalHeight;
  };
}
